import { useEffect, useState } from 'react';
import type { UserSnapshot } from '../../shared/reader';
import type { EthFaucetInfo, MarketSnapshot, ReserveSnapshot } from '../../shared/types';
import { fmtEth } from '../../shared/units';
import { BRAND } from '../brand';
import { AssetName } from '../components/AssetBadge';
import { BrandMark } from '../components/Icons';
import { fmtCooldown, useModals } from '../components/modals';
import { Alert, Amount, Paper, TopBand } from '../components/ui';
import { useEthFaucet } from '../data/queries';
import { fmtAmount, fmtToken, toNumber } from '../lib/format';
import { useWallet } from '../wallet/WalletProvider';

/** Seconds -> "12m" / "1h 05m" for a cooldown that is still running. */
function fmtWait(sec: number): string {
  const m = Math.ceil(sec / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

/** The ETH row's claim button, disabled with the reason (shown under it, so it also reads on touch screens) when this wallet cannot claim right now. */
function EthClaimButton({ info, user, now, connected, wrongNetwork, onClick }: { info: EthFaucetInfo; user: UserSnapshot | undefined; now: number; connected: boolean; wrongNetwork: boolean; onClick(): void }) {
  const max = BigInt(info.maxBalanceWei);
  let label = `Claim ${fmtEth(BigInt(info.dripWei))} ETH`;
  let hint: string | undefined;
  let disabled = !connected || wrongNetwork;
  if (!info.enabled) { disabled = true; hint = `The ETH faucet is unavailable (${info.reason})`; }
  else if (user && user.nativeBalance >= max) { disabled = true; hint = `Only wallets below ${fmtEth(max)} ETH can claim`; }
  else if ((info.nextClaimAt ?? 0) > now) { disabled = true; label = `Ready in ${fmtWait(info.nextClaimAt! - now)}`; hint = 'This wallet claimed recently'; }
  return (
    <span className="claim-cell">
      <button type="button" className="btn btn--primary btn--sm" disabled={disabled} title={hint} onClick={onClick}>{label}</button>
      {disabled && hint && <small>{hint}</small>}
    </span>
  );
}

/**
 * Test assets. Stock tokens are minted by the market's StockFaucet (minting is role-gated in the token, as on mainnet; the
 * faucet's per-token cooldown is 0 since 2026-09-22);
 * USDG is this market's own freely mintable token; ETH for gas comes from the Worker's faucet (wallets below a threshold,
 * after a human check).
 */
export function Faucet({ market, user }: { market: MarketSnapshot; user: UserSnapshot | undefined }) {
  const { status, wrongNetwork, switchNetwork, address } = useWallet();
  const { openFaucet, openEthFaucet, openConnect } = useModals();
  const connected = status === 'connected';
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => { const t = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 15_000); return () => clearInterval(t); }, []);
  const ethInfo = useEthFaucet(address).data;
  const perClaim = (r: ReserveSnapshot) => `${fmtToken(BigInt(r.faucet.amount), r.decimals, 0)} ${r.symbol}`;
  const ethDrip = fmtEth(BigInt(ethInfo?.dripWei ?? '1000000000000000'));
  const ethMax = fmtEth(BigInt(ethInfo?.maxBalanceWei ?? '1000000000000000'));

  return (
    <>
      <TopBand title={
        <div className="market-title market-title--stack">
          <div className="market-title"><BrandMark size={32} /><div><h1>{BRAND.marketName} Faucet</h1></div></div>
          <p>With the testnet faucet you can get free assets to test the protocol. Make sure your wallet is on Robinhood Chain Testnet, then request the assets below. Testnet assets have no monetary value.</p>
        </div>} />
      <main className="page">
        {connected && wrongNetwork && <Alert tone="warning" action={<button type="button" className="btn btn--outlined btn--sm" onClick={() => void switchNetwork().catch(() => undefined)}>Switch network</button>}>Your wallet is on another network. Add or switch to Robinhood Chain Testnet (chain 46630).</Alert>}
        <Paper id="faucet-assets" title="Test assets" right={!connected ? <button type="button" className="btn btn--gradient btn--sm" onClick={openConnect}>Connect wallet</button> : undefined}>
          <div className="table-wrap"><table className="list cards cards--faucet">
            <thead><tr><th>Asset</th><th className="num">Wallet balance</th><th className="num">Per claim</th><th /></tr></thead>
            <tbody>
              <tr>
                <td><AssetName symbol="ETH" extra={<span className="tags"><span className="tag">Gas</span></span>} /></td>
                <td className="num col-balance" data-label="Wallet balance">{user ? fmtAmount(toNumber(user.nativeBalance, 18), 4) : <span className="muted">—</span>}</td>
                <td className="num col-claim" data-label="Per claim">{ethInfo?.faucetAddress
                  ? <>{ethDrip} ETH<small className="muted"> if below {ethMax} ETH, every {fmtCooldown(ethInfo.cooldownSeconds)}</small></>
                  : <span className="muted">—</span>}</td>
                <td className="actions">
                  {ethInfo?.faucetAddress
                    ? <EthClaimButton info={ethInfo} user={user} now={now} connected={connected} wrongNetwork={wrongNetwork} onClick={openEthFaucet} />
                    : <span className="muted small">{ethInfo ? 'Faucet unavailable' : '…'}</span>}
                </td>
              </tr>
              {market.reserves.map((r) => {
                const ur = user?.reserves[r.symbol];
                const wallet = ur?.walletBalance;
                const next = ur?.faucetNextClaimAt ?? null;
                const waiting = r.faucet.kind === 'stock' && next !== null && next > now;
                return (
                  <tr key={r.symbol}>
                    <td><AssetName symbol={r.symbol} /></td>
                    <td className="num col-balance" data-label="Wallet balance">{wallet === undefined ? <span className="muted">—</span> : wallet === 0n ? <span className="muted">0</span> : <Amount raw={wallet} reserve={r} />}</td>
                    <td className="num col-claim" data-label="Per claim">{perClaim(r)}{r.faucet.kind === 'stock' && r.faucet.cooldown > 0 && <small className="muted"> every {fmtCooldown(r.faucet.cooldown)}</small>}</td>
                    <td className="actions">
                      {r.faucet.kind === 'stock' && !r.faucet.enabled
                        ? <span className="muted small">Faucet disabled</span>
                        : <button type="button" className="btn btn--primary btn--sm" disabled={!connected || wrongNetwork || waiting} title={waiting ? `Next claim in ${fmtWait(next! - now)}` : undefined} onClick={() => openFaucet(r.symbol)}>{waiting ? `Ready in ${fmtWait(next! - now)}` : `Claim ${perClaim(r)}`}</button>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
          <p className="muted small">Stock tokens are test tokens of this market with the same contract as the mainnet stock tokens (ERC-8056 multiplier, oracle pause, role-gated minting); the market&apos;s faucet is their only public minter and hands out a fixed amount per claim, as often as you like. USDG is a freely mintable test token: each request mints 10,000. The ETH faucet sends {ethDrip} ETH for gas to wallets holding less than {ethMax} ETH, once per {ethInfo ? fmtCooldown(ethInfo.cooldownSeconds) : 'day'}, after a Cloudflare human check; it pays the gas itself, so a brand-new wallet can start from zero.</p>
        </Paper>
      </main>
    </>
  );
}
